import React, { useState, useRef, useEffect } from 'react';
import {
  Container, Form, Button, Image, Spinner, Alert,
  Row, Col, Card, ProgressBar, Tabs, Tab
} from 'react-bootstrap';
import { faceAPI } from '../services/api';

const FaceComparison = () => {
  const [sourceImage, setSourceImage] = useState(null);
  const [targetImage, setTargetImage] = useState(null);
  const [sourcePreview, setSourcePreview] = useState(null);
  const [targetPreview, setTargetPreview] = useState(null);
  const [similarityThreshold, setSimilarityThreshold] = useState(70);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [boundingBox, setBoundingBox] = useState(null);

  const targetImageRef = useRef(null); // Reference for scaling bounding box

  // Handle image selection
  const handleImageChange = (e, setImage, setPreview) => {
    if (e.target.files[0]) {
      const selectedImage = e.target.files[0];
      setImage(selectedImage);
      setPreview(URL.createObjectURL(selectedImage));
      setResults(null);
      setBoundingBox(null);
    }
  };

  // Compare faces
  const compareFaces = async () => {
    if (!sourceImage || !targetImage) return;

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await faceAPI.compareFaces(sourceImage, targetImage, similarityThreshold);
      setResults(response.data);

      // Extract bounding box if a match is found
      if (response.data.FaceMatches.length > 0) {
        setBoundingBox(response.data.FaceMatches[0].Face.BoundingBox);
      } else {
        setBoundingBox(null);
      }
    } catch (err) {
      setError(`Error comparing faces: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Render face comparison results
  const renderComparisonResults = () => {
    if (!results) return null;

    const { FaceMatches } = results;

    return (
      <div className="result-container">
        <h3 className="mb-4">Face Comparison Results</h3>

        {FaceMatches.length > 0 ? (
          <>
            <Alert variant="success">
              Found {FaceMatches.length} matching face(s) with similarity above {similarityThreshold}%!
            </Alert>
            <Row>
              {FaceMatches.map((match, index) => (
                <Col key={index} md={6} className="mb-4">
                  <Card>
                    <Card.Header className="bg-success text-white">Match #{index + 1}</Card.Header>
                    <Card.Body>
                      <h5>Similarity: {match.Similarity.toFixed(2)}%</h5>
                      <ProgressBar now={match.Similarity} variant="success" className="mb-3" />
                      <Card.Title>Face Details</Card.Title>
                      <div><strong>Confidence:</strong> {match.Face.Confidence.toFixed(2)}%</div>
                      <div><strong>Bounding Box:</strong></div>
                      <ul>
                        <li>Left: {match.Face.BoundingBox.Left.toFixed(2)}</li>
                        <li>Top: {match.Face.BoundingBox.Top.toFixed(2)}</li>
                        <li>Width: {match.Face.BoundingBox.Width.toFixed(2)}</li>
                        <li>Height: {match.Face.BoundingBox.Height.toFixed(2)}</li>
                      </ul>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        ) : (
          <Alert variant="warning">No matching faces found with similarity above {similarityThreshold}%.</Alert>
        )}
      </div>
    );
  };

  // Function to calculate bounding box position and size
  const getBoundingBoxStyle = () => {
    if (!boundingBox || !targetImageRef.current) return {};
    
    const img = targetImageRef.current;
    return {
      position: 'absolute',
      border: '2px solid red',
      left: `${boundingBox.Left * img.width}px`,
      top: `${boundingBox.Top * img.height}px`,
      width: `${boundingBox.Width * img.width}px`,
      height: `${boundingBox.Height * img.height}px`
    };
  };

  return (
    <Container>
      <h2 className="mb-4">Face Comparison</h2>

      <Tabs defaultActiveKey="compare" className="mb-4">
        <Tab eventKey="compare" title="Compare Faces">
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Source Image (Image to Find)</Form.Label>
                  <Form.Control type="file" accept="image/*" onChange={(e) => handleImageChange(e, setSourceImage, setSourcePreview)} />
                  {sourcePreview && <Image src={sourcePreview} className="image-preview mt-3" alt="Source" />}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3 position-relative">
                  <Form.Label>Target Image (Image to Search In)</Form.Label>
                  <Form.Control type="file" accept="image/*" onChange={(e) => handleImageChange(e, setTargetImage, setTargetPreview)} />
                  <div className="position-relative">
                    {targetPreview && (
                      <Image ref={targetImageRef} src={targetPreview} className="image-preview mt-3" alt="Target" />
                    )}
                    {boundingBox && <div className="bounding-box" style={getBoundingBoxStyle()} />}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Similarity Threshold: {similarityThreshold}%</Form.Label>
              <Form.Range
                min={0}
                max={100}
                value={similarityThreshold}
                onChange={(e) => setSimilarityThreshold(parseInt(e.target.value))}
              />
              <Form.Text className="text-muted">
                Only faces with similarity above this threshold will be considered matches.
              </Form.Text>
            </Form.Group>

            <Button variant="primary" onClick={compareFaces} disabled={!sourceImage || !targetImage || loading}>
              {loading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  Comparing...
                </>
              ) : (
                'Compare Faces'
              )}
            </Button>
          </Form>

          {error && <Alert variant="danger" className="mt-4">{error}</Alert>}

          <div className="mt-4">{renderComparisonResults()}</div>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default FaceComparison;
